import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Paper,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  TaskList,
  TaskQuickAdd,
  TaskFilters,
  TaskDetailModal,
} from '../../components/tasks';
import { useTasks } from '../../hooks/useTasks';
import { useTaskMutations } from '../../hooks/useTaskMutations';
import { useClients } from '../../hooks/useClients';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type { TaskFilters as TaskFiltersType, TaskWithClient, CreateTaskRequest, UpdateTaskRequest } from '../../types/task';

export const TasksPage: React.FC = () => {
  // Filters state
  const [filters, setFilters] = useState<TaskFiltersType>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Modal states
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskWithClient | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<TaskWithClient | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Data fetching
  const { data, isLoading, error } = useTasks(filters);
  const { data: clientsData } = useClients();
  const mutations = useTaskMutations();

  // Extract data
  const tasks = data?.data?.tasks || [];
  const pagination = data?.data?.pagination;
  const clients = clientsData?.clients || [];

  // Handlers
  const handleFiltersChange = useCallback((newFilters: TaskFiltersType) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleQuickAdd = async (title: string) => {
    try {
      await mutations.createTask.mutateAsync({ title });
      setSnackbar({
        open: true,
        message: 'Task created successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to create task',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleOpenNewTask = () => {
    setSelectedTask(null);
    setIsDetailModalOpen(true);
  };

  const handleEditTask = (task: TaskWithClient) => {
    setSelectedTask(task);
    setIsDetailModalOpen(true);
  };

  const handleSaveTask = async (data: CreateTaskRequest | UpdateTaskRequest) => {
    try {
      if (selectedTask) {
        await mutations.updateTask.mutateAsync({
          id: selectedTask._id,
          data: data as UpdateTaskRequest,
        });
        setSnackbar({
          open: true,
          message: 'Task updated successfully',
          severity: 'success',
        });
      } else {
        await mutations.createTask.mutateAsync(data as CreateTaskRequest);
        setSnackbar({
          open: true,
          message: 'Task created successfully',
          severity: 'success',
        });
      }
      setIsDetailModalOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to save task',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleToggleComplete = async (task: TaskWithClient) => {
    try {
      if (task.status === 'completed') {
        await mutations.reopenTask.mutateAsync(task._id);
        setSnackbar({
          open: true,
          message: 'Task reopened',
          severity: 'success',
        });
      } else {
        await mutations.completeTask.mutateAsync(task._id);
        setSnackbar({
          open: true,
          message: 'Task completed',
          severity: 'success',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to update task',
        severity: 'error',
      });
    }
  };

  const handleDeleteClick = (task: TaskWithClient) => {
    setTaskToDelete(task);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    try {
      await mutations.deleteTask.mutateAsync(taskToDelete._id);
      setSnackbar({
        open: true,
        message: 'Task deleted successfully',
        severity: 'success',
      });
      setTaskToDelete(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to delete task',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pagination?.total || 0} total tasks
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenNewTask}
        >
          New Task
        </Button>
      </Box>

      {/* Quick Add */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TaskQuickAdd
          onAdd={handleQuickAdd}
          isLoading={mutations.createTask.isPending}
        />
      </Paper>

      {/* Filters */}
      <TaskFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {/* Task List */}
      <TaskList
        tasks={tasks}
        pagination={pagination}
        isLoading={isLoading}
        error={error as Error | null}
        onToggleComplete={handleToggleComplete}
        onEdit={handleEditTask}
        onDelete={handleDeleteClick}
        onPageChange={handlePageChange}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        task={selectedTask}
        onSave={handleSaveTask}
        isLoading={mutations.createTask.isPending || mutations.updateTask.isPending}
        clients={clients}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!taskToDelete}
        title="Delete Task"
        message={`Are you sure you want to delete "${taskToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setTaskToDelete(null)}
        loading={mutations.deleteTask.isPending}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TasksPage;
